import React, { useState, useEffect } from "react";
import Button from "../../ui/common/Button";
import TextArea from "../../ui/common/TextArea";
import Api from "../../../store/api/Api";
import "./EncryptedAuthField.scss";

interface EncryptedAuthFieldProps {
  authEncrypted: string;
  authIv: string;
  authTag: string;
  onDecryptedChange: (decryptedAuth: any) => void;
  onHasUnencryptedChanges?: (hasChanges: boolean) => void;
}

const EncryptedAuthField: React.FC<EncryptedAuthFieldProps> = ({
  authEncrypted,
  authIv,
  authTag,
  onDecryptedChange,
  onHasUnencryptedChanges,
}) => {
  const [isDecrypted, setIsDecrypted] = useState(false);
  const [decryptedAuth, setDecryptedAuth] = useState<string>("");
  const [originalDecryptedAuth, setOriginalDecryptedAuth] = useState<string>("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [decryptError, setDecryptError] = useState<string | null>(null);

  // Reset state when encrypted props change (after re-encryption)
  useEffect(() => {
    if (isDecrypted && authEncrypted) {
      // If we're in decrypted state and the encrypted data changed,
      // it means encryption happened, so we should hide the decrypted view
      setIsDecrypted(false);
      setDecryptedAuth("");
      setOriginalDecryptedAuth("");
      setHasChanges(false);
    }
  }, [authEncrypted, authIv, authTag]);

  const handleDecrypt = async () => {
    setIsDecrypting(true);
    setDecryptError(null);
    try {
      const api = new Api();
      const result = await api.decryptAuth({
        authEncrypted,
        authIv: authIv,
        authTag: authTag,
      });
      const decryptedString = JSON.stringify(result, null, 2);
      setDecryptedAuth(decryptedString);
      setOriginalDecryptedAuth(decryptedString);
      setHasChanges(false);
      setIsDecrypted(true);
      onDecryptedChange(result);
    } catch (error: any) {
      console.error("Failed to decrypt auth:", error);
      setDecryptError(error?.message || "Failed to decrypt authentication data");
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleAuthChange = (value: string) => {
    setDecryptedAuth(value);
    const hasChanges = value !== originalDecryptedAuth;
    setHasChanges(hasChanges);
    
    // Notify parent about unencrypted changes
    if (onHasUnencryptedChanges) {
      onHasUnencryptedChanges(hasChanges);
    }
    
    try {
      const parsed = JSON.parse(value);
      onDecryptedChange(parsed);
    } catch (e) {
      // Invalid JSON, don't update
    }
  };

  const handleHideDecrypted = () => {
    setIsDecrypted(false);
    setDecryptedAuth("");
    setOriginalDecryptedAuth("");
    setHasChanges(false);
    
    // Notify parent that there are no more unencrypted changes
    if (onHasUnencryptedChanges) {
      onHasUnencryptedChanges(false);
    }
    
    onDecryptedChange(null);
  };

  const handleSaveAndEncrypt = async () => {
    setIsEncrypting(true);
    setDecryptError(null);
    try {
      // Parse the JSON to validate and get the object
      const authObject = JSON.parse(decryptedAuth);
      
      // Call encrypt endpoint
      const api = new Api();
      const encryptedResult = await api.encryptAuth(authObject);
      
      // Update the parent with encrypted data
      onDecryptedChange({
        authEncrypted: encryptedResult.authEncrypted,
        authIv: encryptedResult.authIv,
        authTag: encryptedResult.authTag,
      });
      
      // Hide the decrypted view
      setIsDecrypted(false);
      setDecryptedAuth("");
      setOriginalDecryptedAuth("");
      setHasChanges(false);
      
      // Notify parent that changes have been encrypted
      if (onHasUnencryptedChanges) {
        onHasUnencryptedChanges(false);
      }
    } catch (error: any) {
      console.error("Failed to encrypt auth:", error);
      setDecryptError(error?.message || "Failed to encrypt authentication data. Please check your JSON format.");
    } finally {
      setIsEncrypting(false);
    }
  };

  return (
    <div className="encrypted-auth-field">
      <div className="encrypted-auth-header">
        <label className="param-label">
          Encrypted Authentication
          {!isDecrypted && (
            <span className="encrypted-badge">Encrypted</span>
          )}
          {isDecrypted && (
            <span className="encrypted-badge decrypted">Decrypted</span>
          )}
        </label>
        <div className="button-group">
          {!isDecrypted ? (
            <Button
              variant="success"
              size="small"
              onClick={handleDecrypt}
              disabled={isDecrypting}
            >
              {isDecrypting ? "Decrypting..." : "Decrypt & Edit"}
            </Button>
          ) : (
            <>
              {hasChanges ? (
                <Button
                  variant="success"
                  size="small"
                  onClick={handleSaveAndEncrypt}
                  disabled={isEncrypting}
                >
                  {isEncrypting ? "Encrypting..." : "Encrypt"}
                </Button>
              ) : (
                <Button
                  variant="warning"
                  size="small"
                  onClick={handleHideDecrypted}
                >
                  Hide & Lock
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {decryptError && (
        <div className="decrypt-error">
          ⚠️ {decryptError}
        </div>
      )}

      {!isDecrypted ? (
        <div className="encrypted-info">
          <p>
            This crawler configuration uses encrypted authentication.
            Click "Decrypt & Edit" to view and modify the credentials.
          </p>
          <div className="encrypted-preview">
            <small>Encrypted data (read-only):</small>
            <code>{authEncrypted.substring(0, 60)}...</code>
          </div>
        </div>
      ) : (
        <div className="decrypted-auth-editor">
          <p className="editor-hint">
            Edit the authentication object below. Changes will be saved when you click "Save".
          </p>
          <TextArea
            value={decryptedAuth}
            onChange={(e) => handleAuthChange(e.target.value)}
            className="auth-textarea"
          />
        </div>
      )}
    </div>
  );
};

export default EncryptedAuthField;
